'use client';
import { useState, useEffect, useRef } from 'react';

const faqs = [
  {
    q: 'What exactly do I get?',
    a: 'You receive all 43 resource collections shown on this page — Canva & KDP templates, SVGs, fonts, UI kits, stock photos, wall art, eBooks, planners, source code projects, spreadsheets, Notion templates, HR toolkits and much more. Exact file counts, formats and license details are listed on the product page of your chosen marketplace.',
  },
  {
    q: 'Is this one product or 43 separate purchases?',
    a: "One single purchase provides access to all 43 included collections. You check out once and receive everything according to the selected marketplace\'s delivery method.",
  },
  {
    q: 'Is this a subscription?',
    a: 'No. This is a one-time purchase. You pay $79 once and receive the complete bundle with no recurring charges.',
  },
  {
    q: 'Where can I purchase?',
    a: 'You can buy through Etsy, Gumroad or InfiniteMarket — all three options are provided on this page. Choose whichever platform you prefer.',
  },
  {
    q: 'Is the bundle identical on all three marketplaces?',
    a: 'The bundle contains the same 43 collections across platforms. Delivery method and file access details may vary slightly by marketplace — check the specific listing for details.',
  },
  {
    q: 'How are the files delivered?',
    a: 'This is a digital product. After completing your purchase, follow the download and access instructions provided by your chosen marketplace (Etsy, Gumroad or InfiniteMarket).',
  },
  {
    q: 'Can I use the resources commercially?',
    a: 'Usage rights depend on the applicable license terms for each collection. Please review the license information included with the product and on the marketplace listing before any commercial use, resale or redistribution.',
  },
  {
    q: 'Are these physical products?',
    a: 'No. This is an entirely digital product collection. No physical item will be shipped. All files are delivered digitally through the marketplace you purchase from.',
  },
  {
    q: 'Do I need special software?',
    a: 'Requirements vary by collection. Canva templates require Canva, Figma UI kits require Figma, Excel templates require spreadsheet software, and source code projects require relevant development environments. Most collections include clearly labeled file formats.',
  },
  {
    q: 'Can I see what is included before buying?',
    a: 'Yes — scroll up to browse all 43 collections in the catalog section above. You can filter by category and search by keyword to find exactly what you need.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('reveal-hidden');
            entry.target.classList.add('reveal-visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    if (headRef.current) observer.observe(headRef.current);
    if (bodyRef.current) observer.observe(bodyRef.current);
    if (ctaRef.current) observer.observe(ctaRef.current);
    return () => observer.disconnect();
  }, []);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section id="faq" className="relative py-20 lg:py-24 bg-section-dark" aria-labelledby="faq-heading">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14 reveal-hidden" ref={headRef}>
          <h2 id="faq-heading" className="section-heading mb-4" style={{ color: 'var(--foreground)' }}>
            Frequently Asked <span className="text-gradient">Questions</span>
          </h2>
          <p className="text-lg" style={{ color: 'var(--muted-foreground)' }}>
            Everything you need to know before purchasing.
          </p>
        </div>

        <div
          className="reveal-hidden glass rounded-2xl p-6 lg:p-8"
          ref={bodyRef}
          style={{ transitionDelay: '100ms' }}
        >
          {faqs.map((faq, i) => (
            <div key={i} className="faq-item">
              <button
                id={`faq-trigger-${i}`}
                className="faq-trigger"
                aria-expanded={openIndex === i}
                aria-controls={`faq-content-${i}`}
                onClick={() => toggle(i)}
              >
                <span className="pr-4 text-base font-semibold">{faq.q}</span>
                <span className="faq-icon" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
              </button>
              <div
                id={`faq-content-${i}`}
                role="region"
                aria-labelledby={`faq-trigger-${i}`}
                className={`faq-content ${openIndex === i ? 'open' : ''}`}
              >
                {faq.a}
              </div>
            </div>
          ))}
        </div>

        <div
          className="text-center mt-10 reveal-hidden"
          ref={ctaRef}
          style={{ transitionDelay: '200ms' }}
        >
          <p className="mb-4" style={{ color: 'var(--muted-foreground)' }}>Still have questions? The marketplace listing has full details.</p>
          <a
            href="https://www.etsy.com/listing/4530217507/43-premium-digital-product-bundle-canva"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex"
          >
            View Full Listing on Etsy
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" x2="21" y1="14" y2="3" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
